import React, { useState } from "react"
import { twMerge } from "tailwind-merge"

import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { getThemeShapeClasses } from "../utils/themeUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const GallerySectionDesktop = ({ section, theme, highlightedField }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "carousel" // carousel | grid | bento
  const showCaptions = settings.showCaptions || true
  const imageAspectRatio = settings.imageAspectRatio || "auto"
  const enableLightbox = settings.enableLightbox || true
  const lazyLoad = settings.lazyLoad || true
  const [carouselIndex, setCarouselIndex] = useState(0)

  // Get images from section data - support both new and legacy formats
  const images = section.settings?.images || section.images || []

  const isVideo = (item) => item?.type === "video" || /\.(mp4|webm|ogg)$/i.test(item?.url || "")

  const renderMedia = (image, index = 0, explicitSize = null) => {
    if (!image?.url) return null

    const aspectRatioClass = imageAspectRatio === "square" ? "aspect-square" : "aspect-auto"
    // Always use object-cover to fill containers without empty spaces
    const fitClass = "object-cover"

    return (
      <div
        key={index}
        className={twMerge(
          `group relative overflow-hidden ${aspectRatioClass} ${explicitSize ? explicitSize : ""}`,
          getThemeShapeClasses(theme)
        )}
        style={{ backgroundColor: "transparent" }}>
        {isVideo(image) ? (
          <video
            src={image.url}
            className={`h-full w-full ${fitClass}`}
            preload={lazyLoad ? "metadata" : "auto"}
            controls
          />
        ) : (
          <img
            src={image.url}
            alt={image.alt || `Gallery image`}
            className={`h-full w-full ${fitClass} transition-transform duration-300 group-hover:scale-105`}
            loading={lazyLoad ? "lazy" : "eager"}
          />
        )}

        {/* Caption overlay */}
        {showCaptions && image.caption && (
          <div className="absolute inset-x-0 bottom-0 translate-y-full bg-black/50 p-3 text-white transition-transform duration-300 group-hover:translate-y-0">
            <p
              className="text-sm"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
              }}>
              {image.caption}
            </p>
          </div>
        )}

        {/* Lightbox overlay */}
        {enableLightbox && !isVideo(image) && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-all duration-300 group-hover:bg-black/20">
            <svg
              className="h-8 w-8 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              fill="currentColor"
              viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        )}
      </div>
    )
  }

  const renderCarousel = () => {
    const items = images.length > 0 ? images : []
    const item = items[carouselIndex]
    const go = (delta) =>
      setCarouselIndex((prev) => (items.length === 0 ? 0 : (prev + delta + items.length) % items.length))
    return (
      <div className="flex flex-col items-center gap-8 py-24">
        <div
          className={twMerge("relative h-[682px] w-[1160px] overflow-hidden bg-black/5", getThemeShapeClasses(theme))}>
          {item ? (
            <div className="flex h-full w-full items-center justify-center">
              {isVideo(item) ? (
                <video
                  src={item.url}
                  className="h-full w-full object-cover"
                  preload={lazyLoad ? "metadata" : "auto"}
                  controls
                />
              ) : (
                <img src={item.url} alt={item.alt || "Gallery image"} className="h-full w-full object-cover" />
              )}
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-100">No media</div>
          )}
          {items.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous"
                className="absolute left-8 top-1/2 -translate-y-1/2 rounded-full bg-[rgba(0,0,0,0.4)] p-3 text-white transition-transform hover:scale-105"
                onClick={() => go(-1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M15 18l-6-6 6-6"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                aria-label="Next"
                className="absolute right-8 top-1/2 -translate-y-1/2 rounded-full bg-[rgba(0,0,0,0.4)] p-3 text-white transition-transform hover:scale-105"
                onClick={() => go(1)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 6l6 6-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </>
          )}

          {item && isVideo(item) && (
            <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[rgba(0,0,0,0.6)]">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                  <polygon points="9,7 19,12 9,17" />
                </svg>
              </div>
            </div>
          )}
        </div>
        {items.length > 1 && (
          <div className="flex h-3 items-center justify-center gap-2 leading-none">
            {items.map((_, i) => (
              <span
                key={i}
                role="button"
                tabIndex={0}
                className={twMerge(
                  "inline-flex h-2 w-2 flex-none cursor-pointer items-center justify-center rounded-full align-middle",
                  i === carouselIndex ? "bg-[#bd654f]" : "bg-gray-300"
                )}
                onClick={() => setCarouselIndex(i)}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderBento = () => {
    // Use order in settings.images as given by editor; map to bento slots
    const a = images[0] // left tall
    const e = images[1] // top middle-left
    const b = images[2] // top right
    const c = images[3] // bottom middle-left
    const d = images[4] // bottom right
    return (
      <div className="mx-auto grid h-[559px] w-[1160px] grid-cols-4 grid-rows-2 gap-8">
        <div className="col-span-2 row-span-2">
          {a ? (
            isVideo(a) ? (
              <div className={twMerge("relative h-full w-full overflow-hidden", getThemeShapeClasses(theme))}>
                <video
                  src={a.url}
                  className="h-full w-full object-cover"
                  preload={lazyLoad ? "metadata" : "auto"}
                  controls
                />
                {/* Video overlay positioned to match video size */}
                <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[rgba(0,0,0,0.6)]">
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                      <polygon points="9,7 19,12 9,17" />
                    </svg>
                  </div>
                </div>
              </div>
            ) : (
              renderMedia(a, 0, "h-full w-full")
            )
          ) : (
            <div className={twMerge("h-full w-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
        <div className="col-start-3 row-start-1">
          {e ? (
            renderMedia(e, 4, "h-full w-full")
          ) : (
            <div className={twMerge("h-full w-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
        <div className="col-start-4 row-start-1">
          {b ? (
            renderMedia(b, 1, "h-full w-full")
          ) : (
            <div className={twMerge("h-full w-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
        <div className="col-start-3 row-start-2">
          {c ? (
            renderMedia(c, 2, "h-full w-full")
          ) : (
            <div className={twMerge("h-full w-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
        <div className="col-start-4 row-start-2">
          {d ? (
            renderMedia(d, 3, "h-full w-full")
          ) : (
            <div className={twMerge("h-full w-full bg-gray-100", getThemeShapeClasses(theme))} />
          )}
        </div>
      </div>
    )
  }

  const renderGrid = () => {
    const items = images.slice(0, 8)
    while (items.length < 8) items.push(null)
    return (
      <div className="mx-auto grid h-[559px] w-[1160px] grid-cols-4 grid-rows-2 gap-8">
        {items.map((it, i) => (
          <div key={i} className="h-full w-full">
            {it ? (
              renderMedia(it, i, "h-full w-full")
            ) : (
              <div className={twMerge("h-full w-full bg-gray-100", getThemeShapeClasses(theme))} />
            )}
          </div>
        ))}
      </div>
    )
  }

  // Get theme colors with fallback generation
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()
  const sectionBackgroundColor = themeColors.bgLight || "#FFFFFF"

  return (
    <section
      className={twMerge(
        "relative flex min-h-[400px] items-center justify-center py-12",
        isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : ""
      )}
      style={{ backgroundColor: sectionBackgroundColor }}>
      <AnimatedSection className="mx-auto w-full px-0">
        {layout === "bento" && renderBento()}
        {layout === "grid" && renderGrid()}
        {(layout === "carousel" || !layout) && renderCarousel()}
      </AnimatedSection>
    </section>
  )
}

export default GallerySectionDesktop
